import { pack } from '@attain-apps/data-utils'
import { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import { _unionTypeDescription } from './_utils'
import { Id } from './id'
import { Type } from './type'

/**
 * Represents anything that can be used to get the {@link Id} of a
 * {@link Model}
 */
export type Identifiable<
  TType extends Type = Type,
  TPartialId extends PartialId = PartialId,
  TContext extends Id.Context = Id.Context,
> =
  | Readonly<common.Identified<Id<TType, TPartialId, TContext>>>
  | Id<TType, TPartialId, TContext>
  | Id.UsingAnySeparator<TType, TPartialId, TContext>
  | Id.Parts<TType, TPartialId, TContext>
  | Id.Parts.Tuple<TType, TPartialId, TContext>
export namespace Identifiable {
  export function getTypeChecker<
    TType extends Type,
    TContext extends Id.Context,
  >(
    type: TType | readonly TType[],
    isContext: TypeChecker<TContext>,
  ): TypeChecker<Identifiable<TType, PartialId, TContext>> {
    const idTypeChecker = Id.getTypeChecker(type, isContext)
    return TypeChecker.Factory.union(
      common.Identified.getTypeChecker(idTypeChecker),
      idTypeChecker,
      Id.UsingAnySeparator.getTypeChecker(type, isContext),
      Id.Parts.getTypeChecker(type, isContext),
      Id.Parts.Tuple.getTypeChecker(type, isContext),
    ).usingAlias(
      () =>
        `Identifiable<${[
          _unionTypeDescription(...pack(type)),
          PartialId.isPartialId.typeDescription,
          isContext.typeDescription,
        ].join(', ')}>`,
    )
  }
  export const isIdentifiable: TypeChecker<Identifiable> =
    TypeChecker.Factory.lazy(() =>
      getTypeChecker(Type.ALL, Id.Context.isContext),
    )

  /**
   * In cases where the {@link Type} is already known, it is not needed for
   * identifying a model. In those cases, we can make {@link Type} not required
   *
   * @example
   * function getEntityId(entityIdentifiable: Identifiable.ForSpecificType<Type.Entity>): Id<Type.Entity> {
   *   ...
   * }
   *
   * const entityId = getEntityId({ context, partialId })
   */
  export type ForSpecificType<
    TType extends Type = Type,
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > =
    | Identifiable<TType, TPartialId, TContext>
    | Partial.If.Accepts<
        Id.Context.None,
        // NOTE: we cannot Omit type as that would allow identifiables from
        // other types
        Partial.Semi<Id.Parts<TType, TPartialId, TContext>, 'type'>
      >
  export namespace ForSpecificType {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTypeChecker<
      TType extends Type,
      TContext extends Id.Context,
    >(
      type: TType,
      isContext: TypeChecker<TContext>,
    ): TypeChecker<ForSpecificType<TType, PartialId, TContext>> {
      return TypeChecker.Factory.union(
        Identifiable.getTypeChecker(type, isContext),
        TypeChecker.Factory.intersection(
          // Omits type (added back below)
          TypeChecker.Factory.omit(
            Id.Parts.getTypeChecker(
              type,
              isContext(Id.Context.NONE)
                ? // NOTE: Id.Parts.getTypeChecker doesn't allow an optional type
                  // checker, so we need to trick it into allowing it here
                  (TypeChecker.Factory.optional(isContext) as typeof isContext)
                : isContext,
            ),
            ['type'],
          ),
          // Adds back type, optionally
          TypeChecker.Factory.object({
            type: TypeChecker.Factory.optional(TypeChecker.Factory.exact(type)),
          }),
        ),
      ).usingAlias(
        () =>
          `Identifiable.ForSpecificType<${[
            _unionTypeDescription(...pack(type)),
            PartialId.isPartialId.typeDescription,
            isContext.typeDescription,
          ].join(', ')}>`,
      )
    }

    /**
     * Takes a {@link Identifiable.ForSpecificType} and binds it to a
     * {@link Type}, creating a more general {@link Identifiable}
     */
    export function bind<
      TType extends Type,
      TUnboundIdentifiable extends ForSpecificType<TType>,
    >(
      identifiable: TUnboundIdentifiable,
      type: TType,
    ): Coalesce<
      // NOTE: because of the below issue, when the type contains a complete
      // identifiable (which should usually be the case), we use that as our
      // return type. Only when that returns no results, do we then try to infer
      // from the more flexible type below
      TUnboundIdentifiable extends Identifiable ? TUnboundIdentifiable : never,
      // NOTE: TypeScript does not always infer the context correctly here
      TUnboundIdentifiable extends ForSpecificType<
        TType,
        infer TPartialId,
        infer TContext
      >
        ? Identifiable<TType, TPartialId, TContext>
        : never
    >
    export function bind<
      TType extends Type,
      TPartialId extends PartialId,
      TContext extends Id.Context,
    >(
      identifiable: ForSpecificType<TType, TPartialId, TContext>,
      type: TType,
    ): Identifiable<TType, TPartialId, TContext>
    export function bind(
      // NOTE: context needs to be `any` to allow overload signatures to be
      // compatible with implementation signature
      _identifiable: ForSpecificType<any, any>,
      type: Type,
    ): Identifiable {
      // NOTE: this just undoes the `any` type used above
      const identifiable = _identifiable as ForSpecificType
      return Identifiable.isIdentifiable(identifiable)
        ? identifiable
        : {
            ...identifiable,
            context: identifiable.context ?? Id.Context.NONE,
            type,
          }
    }
  }
}
